import { usePerms } from 'contexts/PermsContext';
import usePlaces from 'features/UserMenu/features/Places/hooks/usePlaces';
import useDataRefresh from 'hooks/useDataRefresh';
import useDebounce from 'hooks/useDebounce';
import useLocation from 'hooks/useLocation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { FaHouse, FaLocationDot, FaMagnifyingGlass, FaPen } from 'react-icons/fa6';
import { FiHome } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Popup from 'reactjs-popup';
import ConfirmPopup from '../UI/ConfirmPopup';
import styles from './assets/header.module.css';
import PlacesSearch from './PlacesSearch';

const PlacesSelector = ({ setWidgetRefresh }) => {
  const navigate = useNavigate();
  const [location, setLocation] = useState({});
  const [searching, setSearching] = useState(false);
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [showWaitingForData, setShowWaitingForData] = useState(false);
  const [waitingForDataMessage] = useState('Loading...');
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);
  const locationDropdownRef = useRef(null);
  const debouncedWidgetRefresh = useDebounce(widgetRefreshKey, 1000);
  const {
    places,
    loadingPlaces,
    fetchingPlaces,
    fullSelectedPlaceObject,
    addPlace,
    selectPlace,
    changeHomePlace,
  } = usePlaces();
  const perms = usePerms();
  const dataRefresh = useDataRefresh();
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;

  const selectLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      setLocation(location);
      await selectPlace(location);

      setWidgetRefreshKey(new Date().toString());
    },
    [selectPlace],
  );

  const addLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const newPlaces = await addPlace(location);
      const savedLocation = newPlaces?.find((place) => place.place_id === location.place_id);

      await selectLocation(savedLocation);
    },
    [addPlace, selectLocation],
  );

  const changeHomeLocation = useCallback(
    async (location) => {
      if (!location?.place_id) return;

      const newHomeLocation = await changeHomePlace(location);

      if (newHomeLocation) {
        setLocation(newHomeLocation);
        setWidgetRefreshKey(new Date().toString());
      }
    },
    [changeHomePlace],
  );

  useEffect(() => {
    if (debouncedWidgetRefresh) {
      (async () => {
        setWidgetRefresh(debouncedWidgetRefresh);
        dataRefresh.setWidgetRefresh(debouncedWidgetRefresh);
      })();
    }
  }, [dataRefresh, debouncedWidgetRefresh, setWidgetRefresh]);

  useEffect(() => {
    if (loadingPlaces || fetchingPlaces) return;

    if (places.length === 0) {
      setLocation(null);
      return;
    }

    setLocation(fullSelectedPlaceObject || places[0]);
  }, [places, fullSelectedPlaceObject, loadingPlaces, fetchingPlaces]);

  useEffect(() => {
    if (!dropDownOpen) return undefined;

    const closeDropdownOnPageScroll = (event) => {
      if (locationDropdownRef.current?.contains(event.target)) return;

      setDropDownOpen(false);
    };

    const listenerOptions = { capture: true, passive: true };

    document.addEventListener('scroll', closeDropdownOnPageScroll, listenerOptions);
    window.addEventListener('wheel', closeDropdownOnPageScroll, listenerOptions);
    window.addEventListener('touchmove', closeDropdownOnPageScroll, listenerOptions);

    return () => {
      document.removeEventListener('scroll', closeDropdownOnPageScroll, true);
      window.removeEventListener('wheel', closeDropdownOnPageScroll, true);
      window.removeEventListener('touchmove', closeDropdownOnPageScroll, true);
    };
  }, [dropDownOpen]);

  const permsData = perms.data;
  const canOpenDropdown = places.length > 1;

  if (!permsData) return null;

  const locationStatus = effectiveLocation?.isLoading ? 'checking' : undefined;
  const locationStatusMessage = effectiveLocation?.isLoading ? 'Loading...' : null;

  return (
    <div>
      {(locationStatus === 'checking' || locationStatus === 'error') && locationStatusMessage && (
        <div
          className={`px-2 py-1 small text-truncate ${locationStatus === 'error' ? 'text-warning' : ''}`}
          style={{
            color: locationStatus === 'error' ? undefined : 'var(--color-font)',
            maxWidth: 240,
          }}
          title={locationStatusMessage}
          role={locationStatus === 'error' ? 'alert' : 'status'}
          aria-live={locationStatus === 'error' ? 'assertive' : 'polite'}
        >
          {locationStatusMessage}
        </div>
      )}
      {!searching && location && (
        <Popup
          on={canOpenDropdown ? 'click' : []}
          trigger={(open) => (
            <div
              className={`d-flex align-items-center ${styles['location-pill']}`}
              style={{
                backgroundColor: 'var(--color-primary-highlight)',
                cursor: canOpenDropdown ? 'pointer' : 'default',
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between px-2 w-100"
                style={{
                  fontSize: 16,
                  color: 'var(--color-primary-highlight-foreground)',
                  minWidth: 0,
                }}
              >
                <div className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      background: 'var(--color-background)',
                      color: 'var(--color-font)',
                      height: '24px',
                      width: '24px',
                    }}
                  >
                    {location.DefaultLocationUserHome ? (
                      <FaHouse size={14} />
                    ) : (
                      <FaLocationDot size={14} />
                    )}
                  </div>
                  <Popup
                    trigger={
                      <div
                        className="mx-2 text-truncate fw-medium"
                        style={{ minWidth: 0, flex: 1, lineHeight: 1.1, fontSize: '1.08rem' }}
                      >
                        {location.title}
                      </div>
                    }
                    on={'hover'}
                    position={'top center'}
                    closeOnDocumentClick
                    closeOnEscape
                  >
                    <div className="px-2 py-1 rounded-3 bg-white text-black">{location.title}</div>
                  </Popup>
                </div>
                <div className="d-flex align-items-center justify-content-end" style={{ gap: 10 }}>
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: 16,
                      color: 'var(--color-primary-highlight-foreground)',
                      opacity: perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces'])
                        ? 1
                        : 0,
                      pointerEvents: perms.testPermission(
                        [8, 9, 10, 11, 12, 13],
                        ['Create-EditPlaces'],
                      )
                        ? 'auto'
                        : 'none',
                    }}
                    onClick={() => {
                      navigate('/usermenu?tab=Places');
                    }}
                  >
                    <FaPen size={14} />
                  </button>
                  <button
                    type="button"
                    className=" w-100 border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                    style={{ color: 'var(--color-primary-highlight-foreground)' }}
                    onClick={() => {
                      setSearching(true);
                      setDropDownOpen(false);
                    }}
                  >
                    <FaMagnifyingGlass size={14} />
                  </button>
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ width: 16 }}
                  >
                    <BsChevronDown size={14} />
                  </div>
                </div>
              </div>
            </div>
          )}
          open={canOpenDropdown ? dropDownOpen : false}
          onOpen={() => {
            if (canOpenDropdown) setDropDownOpen(true);
          }}
          onClose={() => setDropDownOpen(false)}
          position={'bottom center'}
          closeOnDocumentClick
          closeOnEscape
        >
          {canOpenDropdown && (
            <div
              ref={locationDropdownRef}
              className={`overflow-y-auto ${styles['location-dropdown']}`}
              style={{
                height: 200,
                minWidth: 240,
                width: '100%',
                backgroundColor: 'var(--color-primary-highlight)',
                color: 'var(--color-primary-highlight-foreground)',
              }}
            >
              {places &&
                places.length > 0 &&
                places.map((place, index) => (
                  <div
                    key={place.place_id}
                    value={place.place_id}
                    className={`d-flex cursor-pointer w-100 ${styles['location-dropdown-item']}`}
                    style={{
                      borderBottom:
                        index !== places.length - 1 && '1px solid rgba(255,255,255,0.25)',
                    }}
                    onClick={() => {
                      selectLocation(place);
                      setDropDownOpen(false);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center w-100 px-2">
                      <div className="text-truncate pe-2" style={{ fontSize: '1.05rem' }}>
                        {place.title}
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 20,
                          color: 'var(--color-primary-highlight-foreground)',
                          opacity: place.DefaultLocationUserHome ? 1 : 0.85,
                          cursor:
                            !place.DefaultLocationUserHome &&
                            perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable'])
                              ? 'pointer'
                              : 'default',
                        }}
                        onClick={(e) => {
                          if (
                            !place.DefaultLocationUserHome &&
                            perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable'])
                          ) {
                            e.stopPropagation();
                            changeHomeLocation(place);
                          }
                        }}
                      >
                        {place.DefaultLocationUserHome ? (
                          <FaHouse size={16} />
                        ) : (
                          <FiHome size={16} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Popup>
      )}
      {searching && (
        <div>
          <PlacesSearch
            places={places}
            addLocation={addLocation}
            selectLocation={selectLocation}
            setSearching={setSearching}
            setWidgetRefresh={setWidgetRefresh}
            quickLook
            cwValidated
          />
        </div>
      )}
      {showWaitingForData && (
        <ConfirmPopup
          open={showWaitingForData}
          setOpen={setShowWaitingForData}
          message={waitingForDataMessage}
          options={['Cancel']}
          callbacks={[
            () => {
              setShowWaitingForData(false);
            },
          ]}
          closeAction={() => {
            setShowWaitingForData(false);
          }}
          buttonStyles={[
            { backgroundColor: 'var(--color-background)', color: 'var(--color-font)' },
          ]}
        />
      )}
    </div>
  );
};

export default PlacesSelector;
